import { Button, CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { useRecordingsSearchContext } from '../../../hooks';
import {
  ListenRecordingModal,
  ReadCallNotesModal,
  StoplightAnalysisModal,
} from '../../Modals';

const STATE = {
  HAS_DATA: 'hasData',
  HAS_NO_DATA: 'hasNoData',
  IS_LOADING: 'isLoading',
  IS_ERROR: 'isError',
};

const getState = (data, isLoading, isError, isSearchByContactNameValid) => {
  if (isLoading) return STATE.IS_LOADING;
  if (isError) return STATE.IS_ERROR;
  const hasNoData = data.length === 0;
  if (hasNoData || !isSearchByContactNameValid) return STATE.HAS_NO_DATA;
  return STATE.HAS_DATA;
};

export const Playback = () => {
  const { requestBody, isSearchByContactNameValid } =
    useRecordingsSearchContext();
  const [callRecordings, setCallRecordings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const abortControllerRef = useRef(null);

  const fetchCallRecordings = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    if (!isSearchByContactNameValid) {
      setCallRecordings([]);
      setIsLoading(false);
      setIsError(false);
      return;
    }

    try {
      const response = await fetch('/call-recordings', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        ...(requestBody && {
          body: JSON.stringify(requestBody),
        }),
      });

      if (response.status !== 200) throw new Error();

      const data = await response.json();
      setCallRecordings(data.callRecordings?.query1 ?? []);
      setIsLoading(false);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsLoading(false);
      setIsError(true);
      toast.error('Failed to load call recordings!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, [requestBody, isSearchByContactNameValid]);

  useEffect(() => {
    fetchCallRecordings();
  }, [fetchCallRecordings]);

  const [currentRec, setCurrentRec] = useState({});
  const [currentCLTranscript, setCurrentCLTranscript] = useState([]);
  const [isListenRecordingModalOpened, setIsListenRecordingModalOpened] =
    useState(false);
  const [isReadCallNotesModalOpened, setIsReadCallNotesModalOpened] =
    useState(false);
  const [isStoplightAnalysisModalOpened, setIsStoplightAnalysisModalOpened] =
    useState(false);

  const openListenRecordingModal = () => setIsListenRecordingModalOpened(true);
  const closeListenRecordingModal = () =>
    setIsListenRecordingModalOpened(false);
  const openReadCallNotesModal = () => setIsReadCallNotesModalOpened(true);
  const closeReadCallNotesModal = () => setIsReadCallNotesModalOpened(false);
  const openStoplightAnalysisModal = () =>
    setIsStoplightAnalysisModalOpened(true);
  const closeStoplightAnalysisModal = () =>
    setIsStoplightAnalysisModalOpened(false);

  const [hmmNumber, setHmmNumber] = useState(0);
  const [confirmedContact, setConfirmedContact] = useState(false);
  const [callLength, setCallLength] = useState(0);
  const [callerTalkTime, setCallerTalkTime] = useState(0);
  const [followedScript, setFollowedScript] = useState(0);
  const [analysisScore, setAnalysisScore] = useState(0);

  const callTypeTitle = {
    NoAns: 'No Answer',
    FirstAppt: 'Booked',
    HangUp: 'Hung Up',
    EmailSent: 'Email',
    Callback: 'Callback',
    NotInterested: 'Not Interested',
    DoNotCall: 'Do Not Call',
  };

  const localTime = moment();
  const chicagoTime = moment.tz('America/Chicago');
  const timeDiff = localTime.utcOffset() - chicagoTime.utcOffset();

  const getLocalTimeStampForRecording = (timestamp, format) => {
    const centralTime = moment.utc(timestamp);
    const localUserTime = centralTime.clone().add(timeDiff, 'minutes');

    return localUserTime.format(format);
  };
  const convertToUserLocalTime = (
    dateStr,
    format = 'MMMM Do, YYYY h:mm A z',
  ) => {
    let appendedOffset = dateStr.replace('.000Z', '-05:00');
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return moment(appendedOffset).tz(userTimezone).format(format);
  };

  const initStoplightValues = () => {
    setFollowedScript(0);
    setCallerTalkTime(0);
    setHmmNumber(0);
    setConfirmedContact(0);
    setAnalysisScore(0);
  };

  function checkForConfirmationRequest(callerTranscripts) {
    const wholeText = callerTranscripts.map(item => item.text).join(' ');
    const emailPattern1 = /Can you confirm your email (address)?/i;
    const emailPattern2 = /I have your email as/i;
    const emailPattern3 = /can I confirm that your email/i;
    const phonePattern1 = /Can you confirm your phone (number)?/i;
    const phonePattern2 = /What is the (best)? phone (number)? to reach you/i;

    const asksForPhoneConfirmation =
      phonePattern1.test(wholeText) || phonePattern2.test(wholeText);
    const asksForEmailConfirmation =
      emailPattern1.test(wholeText) ||
      emailPattern2.test(wholeText) ||
      emailPattern3.test(wholeText);

    return asksForPhoneConfirmation || asksForEmailConfirmation;
  }

  function countFillerWords(callerTranscripts) {
    const wholeText = callerTranscripts.map(item => item.text).join(' ');
    const cleanedText = wholeText.replace(/[^\w\s]/g, '').toLowerCase();
    const fillerWords = ['hmm', 'umm', 'uh', 'ah', 'um', 'uhm', 'mhm'];
    const pattern = new RegExp(`\\b(${fillerWords.join('|')})\\b`, 'gi');
    const matches = wholeText.match(pattern);
    const count = matches ? matches.length : 0;

    return count;
  }

  function calcFollowedScript(transcriptCL) {
    const calllerTranscripts = transcriptCL
      .filter(obj => obj.channel === '1')
      .map(item => item.text)
      .join(' ');
    const wholeTranscripts = transcriptCL.map(item => item.text).join(' ');
    const callerText = calllerTranscripts.replace(/[^\w\s]/g, '').toLowerCase();
    const wholeText = wholeTranscripts.replace(/[^\w\s]/g, '').toLowerCase();
    const callerWords = callerText.split(/\s+/).filter(Boolean);
    const allWords = wholeText.split(/\s+/).filter(Boolean);
    const totalWordsInCaller = callerWords.length;
    const totalWordsInText = allWords.length;
    const percentage = ((totalWordsInCaller / totalWordsInText) * 100).toFixed(
      0,
    );

    return percentage;
  }

  function calculateCallerTalkTime(transcriptCL) {
    const callerTimeArr = transcriptCL
      .filter(obj => obj.channel === '1')
      .map(item => item.end - item.start);
    const wholeTimeArr = transcriptCL.map(item => item.end - item.start);
    const callerTime = callerTimeArr.reduce((acc, curr) => acc + curr, 0);
    const wholeTime = wholeTimeArr.reduce((acc, curr) => acc + curr, 0);
    const percentage = ((callerTime / wholeTime) * 100).toFixed(0);

    return percentage;
  }

  useEffect(() => {
    if (currentRec.CLTranscript) {
      const transcriptCL = JSON.parse(currentRec.CLTranscript);
      if (transcriptCL) {
        const calllerTranscript = transcriptCL.filter(
          obj => obj.channel === '1',
        );
        if (calllerTranscript.length) {
          const followedScriptPercent = calcFollowedScript(transcriptCL);
          const callerTimePercent = calculateCallerTalkTime(transcriptCL);
          const hmmCount = countFillerWords(calllerTranscript);
          const contactConfirmStatus =
            checkForConfirmationRequest(calllerTranscript);
          setCallerTalkTime(callerTimePercent);
          setFollowedScript(followedScriptPercent);
          setHmmNumber(hmmCount);
          setConfirmedContact(contactConfirmStatus);
          const followedScriptScore = Math.max(
            0,
            45 - 0.5 * (100 - followedScriptPercent),
          );
          var callerTimeScore = 30;
          if (callerTimePercent > 60) {
            callerTimeScore -= 0.5 * (callerTimePercent - 60);
          } else if (callerTimePercent < 40) {
            callerTimeScore -= 0.5 * (40 - callerTimePercent);
          }
          const callLengthScore = Math.max(
            0,
            20 - (5 * (300 - callLength)) / 60,
          );
          const confirmScore = 5;
          const totalScore =
            followedScriptScore +
            callerTimeScore +
            callLengthScore +
            confirmScore -
            hmmNumber;
          setAnalysisScore(totalScore);
        } else {
          initStoplightValues();
        }
      }
    } else {
      initStoplightValues();
    }

    const audio = new Audio(currentRec.RecordingURL);
    const handleMetadata = () => {
      setCallLength(audio.duration);
    };
    audio.addEventListener('loadedmetadata', handleMetadata);

    return () => {
      audio.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [currentRec, callLength]);

  const state = getState(
    callRecordings,
    isLoading,
    isError,
    isSearchByContactNameValid,
  );

  return (
    <div className="overflow-auto max-w-full flex-1">
      <div className="rounded-xl round gap-3 items-center max-h-[380px] h-max-h-[480px] overflow-auto playback-scrollbar pr-2 h-full">
        {state === STATE.IS_LOADING && (
          <div className="flex justify-center items-center h-full">
            <CircularProgress size={80} sx={{ color: '#1F68A2' }} />
          </div>
        )}
        {state === STATE.IS_ERROR && (
          <div className="flex justify-center items-center h-full flex-col gap-6">
            <div className="flex justify-center items-center flex-col gap-3">
              <span className="text-2xl font-medium">
                Failed to load call recordings
              </span>
              <span className="text-lg font-medium text-[#6b7280]">
                Error occurred while loading. Try again.
              </span>
            </div>
            <Button
              disableFocusRipple
              disableRipple
              variant="text"
              sx={{
                color: '#FF344C',
                fontFamily: 'inherit',
                textTransform: 'none',
                fontSize: 21,
                padding: 0,
                '&.MuiButtonBase-root:hover': {
                  backgroundColor: 'transparent',
                  color: '#FF5A6D',
                },
              }}
              onClick={fetchCallRecordings}
            >
              Reload
            </Button>
          </div>
        )}
        {state === STATE.HAS_NO_DATA && (
          <div className="flex justify-center items-center h-full">
            <p>There are no records to display</p>
          </div>
        )}
        {state === STATE.HAS_DATA &&
          callRecordings.map((item, index) => (
            <div
              key={index}
              className="flex min-w-min lg:text-xs 2xl:text-base gap-3 rounded-[32px] bg-secondary/10 lg:p-1 2xl:p-2 mt-3"
              style={{ color: '#0F0F74' }}
            >
              <div className="min-w-32 flex-1 flex items-center text-center justify-center font-semibold">
                {item.ContactName}
              </div>
              <div className="min-w-28 flex-1 flex-col">
                <div className="flex items-center">
                  <span className="inline-block lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                      ></path>
                    </svg>
                  </span>
                  <span className="ml-2 font-semibold whitespace-nowrap">
                    {convertToUserLocalTime(item.Timestamp, 'MM-DD-YYYY')}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="inline-block lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  </span>
                  <span className="ml-2 font-semibold whitespace-nowrap">
                    {convertToUserLocalTime(item.Timestamp, 'hh:mm:ss A')}
                  </span>
                </div>
              </div>
              <div className="min-w-28 flex items-center text-center justify-center font-semibold">
                {callTypeTitle[item.CallType]}
              </div>
              <div className="min-w-28 flex items-center gap-3">
                <span
                  className={`cursor-pointer inline-block lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 flex items-center justify-center ${item.RecordingURL ? '' : 'pointer-events-none opacity-50'}`}
                  onClick={() => {
                    openListenRecordingModal();
                    setCurrentRec(item);
                  }}
                  data-hs-overlay="#recording-playback-modal"
                >
                  <svg
                    width="13"
                    height="19"
                    viewBox="0 0 13 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6.5 13.1034C7.53401 13.1024 8.52537 12.6879 9.25653 11.9509C9.98769 11.2139 10.3989 10.2147 10.4 9.17241V3.93103C10.4 2.88846 9.98911 1.88859 9.25772 1.15137C8.52632 0.414161 7.53434 0 6.5 0C5.46566 0 4.47368 0.414161 3.74228 1.15137C3.01089 1.88859 2.6 2.88846 2.6 3.93103V9.17241C2.60108 10.2147 3.01231 11.2139 3.74347 11.9509C4.47463 12.6879 5.46599 13.1024 6.5 13.1034ZM3.9 3.93103C3.9 3.23598 4.17393 2.5694 4.66152 2.07793C5.14912 1.58645 5.81044 1.31034 6.5 1.31034C7.18956 1.31034 7.85088 1.58645 8.33848 2.07793C8.82607 2.5694 9.1 3.23598 9.1 3.93103V9.17241C9.1 9.86746 8.82607 10.534 8.33848 11.0255C7.85088 11.517 7.18956 11.7931 6.5 11.7931C5.81044 11.7931 5.14912 11.517 4.66152 11.0255C4.17393 10.534 3.9 9.86746 3.9 9.17241V3.93103ZM7.15 15.6914V18.3448C7.15 18.5186 7.08152 18.6852 6.95962 18.8081C6.83772 18.931 6.67239 19 6.5 19C6.32761 19 6.16228 18.931 6.04038 18.8081C5.91848 18.6852 5.85 18.5186 5.85 18.3448V15.6914C4.24746 15.527 2.76231 14.7695 1.68175 13.5654C0.601186 12.3613 0.00197934 10.796 0 9.17241C0 8.99865 0.0684821 8.83201 0.190381 8.70914C0.312279 8.58627 0.477609 8.51724 0.65 8.51724C0.822391 8.51724 0.987721 8.58627 1.10962 8.70914C1.23152 8.83201 1.3 8.99865 1.3 9.17241C1.3 10.5625 1.84786 11.8957 2.82304 12.8786C3.79823 13.8616 5.12087 14.4138 6.5 14.4138C7.87913 14.4138 9.20177 13.8616 10.177 12.8786C11.1521 11.8957 11.7 10.5625 11.7 9.17241C11.7 8.99865 11.7685 8.83201 11.8904 8.70914C12.0123 8.58627 12.1776 8.51724 12.35 8.51724C12.5224 8.51724 12.6877 8.58627 12.8096 8.70914C12.9315 8.83201 13 8.99865 13 9.17241C12.998 10.796 12.3988 12.3613 11.3183 13.5654C10.2377 14.7695 8.75254 15.527 7.15 15.6914Z"
                      fill="#0F0F74"
                    />
                  </svg>
                </span>
                <span
                  className={`cursor-pointer inline-block lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 flex items-center justify-center ${item.CLTranscript && item.CLTranscript !== 'null' ? '' : 'pointer-events-none opacity-50'}`}
                  onClick={() => {
                    openReadCallNotesModal();
                    setCurrentRec(item);
                    if (item && item.CLTranscript) {
                      setCurrentCLTranscript(JSON.parse(item.CLTranscript));
                    }
                  }}
                >
                  <svg
                    width="15"
                    height="19"
                    viewBox="0 0 15 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.8006 5.32913L10.0278 0.21375C9.96447 0.14591 9.88926 0.0921149 9.80649 0.055439C9.72372 0.0187631 9.63502 -7.52589e-05 9.54545 2.25953e-07H1.36364C1.00198 2.25953e-07 0.655131 0.153983 0.3994 0.428075C0.143668 0.702166 0 1.07391 0 1.46154V17.5385C0 17.9261 0.143668 18.2978 0.3994 18.5719C0.655131 18.846 1.00198 19 1.36364 19H13.6364C13.998 19 14.3449 18.846 14.6006 18.5719C14.8563 18.2978 15 17.9261 15 17.5385V5.84615C15.0001 5.75016 14.9825 5.65509 14.9483 5.56638C14.9141 5.47767 14.8639 5.39705 14.8006 5.32913ZM10.2273 2.49466L12.6724 5.11539H10.2273V2.49466ZM13.6364 17.5385H1.36364V1.46154H8.86364V5.84615C8.86364 6.03997 8.93547 6.22584 9.06334 6.36289C9.1912 6.49993 9.36462 6.57692 9.54545 6.57692H13.6364V17.5385ZM10.9091 10.2308C10.9091 10.4246 10.8373 10.6105 10.7094 10.7475C10.5815 10.8845 10.4081 10.9615 10.2273 10.9615H4.77273C4.5919 10.9615 4.41847 10.8845 4.29061 10.7475C4.16274 10.6105 4.09091 10.4246 4.09091 10.2308C4.09091 10.037 4.16274 9.85108 4.29061 9.71404C4.41847 9.57699 4.5919 9.5 4.77273 9.5H10.2273C10.4081 9.5 10.5815 9.57699 10.7094 9.71404C10.8373 9.85108 10.9091 10.037 10.9091 10.2308ZM10.9091 13.1538C10.9091 13.3477 10.8373 13.5335 10.7094 13.6706C10.5815 13.8076 10.4081 13.8846 10.2273 13.8846H4.77273C4.5919 13.8846 4.41847 13.8076 4.29061 13.6706C4.16274 13.5335 4.09091 13.3477 4.09091 13.1538C4.09091 12.96 4.16274 12.7742 4.29061 12.6371C4.41847 12.5001 4.5919 12.4231 4.77273 12.4231H10.2273C10.4081 12.4231 10.5815 12.5001 10.7094 12.6371C10.8373 12.7742 10.9091 12.96 10.9091 13.1538Z"
                      fill="#0F0F74"
                    />
                  </svg>
                </span>
                <div className="relative flex flex-col items-center group">
                  <span
                    className="cursor-pointer inline-block lg:w-5 lg:h-5 2xl:w-6 2xl:h-6 flex items-center justify-center"
                    onClick={() => {
                      setCurrentRec(item);
                      openStoplightAnalysisModal();
                    }}
                  >
                    <svg
                      width="18"
                      height="19"
                      viewBox="0 0 18 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_432_384)">
                        <path
                          d="M17.25 10.9615H15.75V5.11538H17.25C17.6625 5.11538 18 4.79385 18 4.38462C18 3.97538 17.6625 3.65385 17.25 3.65385H15.75V1.46154C15.75 0.657692 15.0825 0 14.25 0H3.75C2.925 0 2.25 0.657692 2.25 1.46154V3.65385H0.75C0.3375 3.65385 0 3.98269 0 4.38462C0 4.78654 0.3375 5.11538 0.75 5.11538H2.25V10.9615H0.75C0.3375 10.9615 0 11.2904 0 11.6923C0 12.0942 0.3375 12.4231 0.75 12.4231H2.25V17.5385C2.25 18.3496 2.925 19 3.75 19H14.25C15.0825 19 15.75 18.3496 15.75 17.5385V12.4231H17.25C17.6625 12.4231 18 12.1015 18 11.6923C18 11.2831 17.6625 10.9615 17.25 10.9615ZM14.25 17.5385H3.75V1.46154H14.25V17.5385Z"
                          fill="#0F0F74"
                        />
                        <path
                          d="M8.99953 7.20508C7.70203 7.20508 6.64453 8.23546 6.64453 9.49969C6.64453 10.7639 7.69453 11.7943 8.99953 11.7943C10.3045 11.7943 11.3545 10.7712 11.3545 9.49969C11.3545 8.22816 10.3045 7.20508 8.99953 7.20508ZM8.99953 10.647C8.35453 10.647 7.82203 10.1355 7.82203 9.49969C7.82203 8.86392 8.35453 8.35239 8.99953 8.35239C9.64453 8.35239 10.177 8.87123 10.177 9.49969C10.177 10.1282 9.65203 10.647 8.99953 10.647Z"
                          fill="#0F0F74"
                        />
                        <path
                          d="M8.99953 12.3569C7.70203 12.3569 6.64453 13.3873 6.64453 14.6515C6.64453 15.9158 7.69453 16.9462 8.99953 16.9462C10.3045 16.9462 11.3545 15.9231 11.3545 14.6515C11.3545 13.38 10.3045 12.3569 8.99953 12.3569ZM8.99953 15.7989C8.35453 15.7989 7.82203 15.2873 7.82203 14.6515C7.82203 14.0158 8.35453 13.5042 8.99953 13.5042C9.64453 13.5042 10.177 14.0231 10.177 14.6515C10.177 15.28 9.65203 15.7989 8.99953 15.7989Z"
                          fill="#0F0F74"
                        />
                        <path
                          d="M8.99953 2.05322C7.70203 2.05322 6.64453 3.08361 6.64453 4.34784C6.64453 5.61207 7.69453 6.64245 8.99953 6.64245C10.3045 6.64245 11.3545 5.61938 11.3545 4.34784C11.3545 3.0763 10.3045 2.05322 8.99953 2.05322ZM8.99953 5.49515C8.35453 5.49515 7.82203 4.98361 7.82203 4.34784C7.82203 3.71207 8.35453 3.20053 8.99953 3.20053C9.64453 3.20053 10.177 3.71938 10.177 4.34784C10.177 4.9763 9.65203 5.49515 8.99953 5.49515Z"
                          fill="#0F0F74"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_432_384">
                          <rect width="18" height="19" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>

      {isListenRecordingModalOpened && (
        <ListenRecordingModal
          open={isListenRecordingModalOpened}
          onClose={closeListenRecordingModal}
          selectedRecording={currentRec}
          getLocalTimeStampForRecording={getLocalTimeStampForRecording}
        />
      )}
      {isReadCallNotesModalOpened && (
        <ReadCallNotesModal
          open={isReadCallNotesModalOpened}
          onClose={closeReadCallNotesModal}
          selectedRecording={currentRec}
          getLocalTimeStampForRecording={getLocalTimeStampForRecording}
          currentCLTranscript={currentCLTranscript}
        />
      )}
      {isStoplightAnalysisModalOpened && (
        <StoplightAnalysisModal
          open={isStoplightAnalysisModalOpened}
          onClose={closeStoplightAnalysisModal}
          selectedRecording={currentRec}
          getLocalTimeStampForRecording={getLocalTimeStampForRecording}
          callLength={callLength}
          hmmNumber={hmmNumber}
          callerTalkTime={callerTalkTime}
          followedScript={followedScript}
          analysisScore={analysisScore}
        />
      )}
    </div>
  );
};
